import classNames from "classnames";
import React from "react";
import { Button } from "semantic-ui-react";
import Tag from "../../components/Tag/Tag";
import { formatCurrency } from "../../utilities";
import { type Prices } from "../models";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";

interface Props {
  billingCycle: "monthly" | "yearly"
  numberOfUsers: number
  onNumberOfUsersChanged: (newNumberOfUsers: number) => void
  discount?: number
  isSelected: boolean
  isOtherSelected: boolean
  onSelected: () => void
  error: boolean
  pricing: server.dto.ISubscriptionPricing
}

const getPrices = (props: Props): Prices => {
  const totalUserPricePerMonth = props.pricing.pricePerUserPerMonth * props.numberOfUsers;
  const totalPricePerMonth = props.pricing.basePricePerMonth + totalUserPricePerMonth;

  if (props.billingCycle === "monthly") {
    return {
      basePricePerMonth: props.pricing.basePricePerMonth,
      pricePerUserPerMonth: props.pricing.pricePerUserPerMonth,
      totalUserPricePerMonth: totalUserPricePerMonth,
      totalPricePerMonth: totalPricePerMonth,
      totalPricePerYear: totalPricePerMonth * 12
    };
  } else { // yearly
    return {
      basePricePerMonth: props.pricing.basePricePerMonth,
      pricePerUserPerMonth: props.pricing.pricePerUserPerMonth,
      totalUserPricePerMonth: totalUserPricePerMonth,
      totalPricePerMonth: totalPricePerMonth,
      totalPricePerYear: totalPricePerMonth * 12
    };
  }
};

const SubscriptionPricing = (props: Props) => {
  const { isSelected, error } = props;

  const headerLabel = props.billingCycle === "monthly" ? "Monthly plan" : "Annual plan";

  const prices = getPrices(props);

  const currency = props.pricing.currency;

  const panelClasses = classNames("padding-big flex-column",
    {
      "gray-100": !isSelected && !error,
      "blue-bg": isSelected && !error,
      "lightred-bg": error,
      "white-text": isSelected,
      s1_transparent60: props.isOtherSelected
    });

  return <div className={panelClasses} style={{ flex: "0 0 48%" }}>
    <div className="grow1">

      <div className="flex margin-bottom-huge">
        <div className="grow1">
          <h2>{headerLabel}</h2>
        </div>
        {props.discount && !isSelected && <Tag
          size="larger"
          style={{ marginRight: 0 }}
          label={`Save ${props.discount}%`}
          color="orange" />}
      </div>

      <div className="flex margin-bottom">
        <div className="grow1 big_text">
          {RESX.Subscription.BasePrice}
        </div>
        <div className="big_text nowrap">
          {formatCurrency(prices.basePricePerMonth, currency, serverReference.culture.uiLocale)}
        </div>
      </div>

      <div className="flex align-center margin-bottom-huge">
        <div className="grow1 margin-right">
          <PricePerUserDropdown
            numberOfUsers={props.numberOfUsers}
            onNumberOfUsersChanged={props.onNumberOfUsersChanged}
          />
        </div>
        <div className="big_text margin-right s1_gray">
          X
        </div>
        <div className="big_text margin-right-huge s1_gray">
          {formatCurrency(prices.pricePerUserPerMonth, currency, serverReference.culture.uiLocale)}
        </div>
        <div className="big_text nowrap">
          {formatCurrency(prices.totalUserPricePerMonth, currency, serverReference.culture.uiLocale)}
        </div>
      </div>

      <div className="flex margin-bottom" style={{
        fontWeight: 500
      }}>
        <div className="grow1 big_text">
          {RESX.DateTime.PerMonth}
        </div>
        <div className="big_text nowrap">
          {formatCurrency(prices.totalPricePerMonth, currency, serverReference.culture.uiLocale)}
        </div>
      </div>

      {props.billingCycle === "yearly" && <div className="flex margin-bottom-huge">
        <div className="grow1 big_text">
          {RESX.DateTime.PerYear}
        </div>
        <div className="big_text nowrap">
          {formatCurrency(prices.totalPricePerYear, currency, serverReference.culture.uiLocale)}
        </div>
      </div>}

      {props.billingCycle !== "yearly" && <div className="flex margin-bottom-huge">
        <div className="grow1 big_text">
          &nbsp;
        </div>
      </div>}

    </div>

    {
      !isSelected && <div>
        <Button
          fluid
          onClick={props.onSelected}
          disabled={props.isSelected}
          color="blue"
        >
          {RESX.Buttons.Choose}
        </Button>
      </div>
    }
  </div >;
};
interface UserOption {
  value: number
};
const PricePerUserDropdown = (props: { numberOfUsers: number, onNumberOfUsersChanged: (newNumberOfUsers: number) => void }) => {
  const options = new Array(50).fill(0).map<UserOption>((_, i) => ({
    value: i + 1
  }));
  return <BaseReactSelectDropdown
    value={options.find(o => o.value === props.numberOfUsers)}
    options={options}
    onChange={o => { props.onNumberOfUsersChanged(o.value); }}
    getLabel={item => `${item.value} users`}
    getValue={item => item.value}
    excludeKeys={[]}
    config={{}}
  />;
};

export default SubscriptionPricing;